import React, { useState, useEffect } from "react"
import { times } from 'lodash'
import Head from 'next/head'
import InfiniteScroll from 'react-infinite-scroll-component'
import ScrollableAnchor from "react-scrollable-anchor"
import Link from 'next/link'

import { urlParser } from '@/util/imgHelpers'
import HomePageImage from "../HomePageImage"
import LockDisplay from '../../components/LockDisplay'
import BlogSerializer from '../Blog/BlogSerializer'
import Error from '@/components/Error'

import { useGetBlogs } from 'src/hooks/blogs/useGetBlogs'

const Grid = ({
    isMobile,
    subData
  }) => {
    const STARTING_COUNT = 8
    const { fetchData, error } = useGetBlogs();
    const [blogData, setBlogData] = useState([])
    const [itemCount, setItemCount] = useState(STARTING_COUNT)
    const [hasMore, setHasMore] = useState(true)
    const blogCountArray = times(itemCount < blogData.length ? itemCount : blogData.length, Number)

    useEffect(() => {
      fetchData({ limit: itemCount }).then(res => {
        setBlogData(res)
      }).catch(e => console.error(e))
    }, [])

    const displayMoreBlogs = () => {
      fetchData({ limit: STARTING_COUNT, skip: itemCount }).then(res => {
        if (!res.length) return setHasMore(false)

        setBlogData([...blogData, ...res])
        setItemCount((prev) => prev + STARTING_COUNT)
      }).catch(e => console.error(e))
    }
    
    const getIsPermitted = (index) => {
      if (process.env.NODE_ENV === 'development') return true
      switch (blogData[index].subscriptionRequired) {
        case 'free': return true
        case 'Standard':
          // eslint-disable-next-line sonarjs/prefer-single-boolean-return
          if (subData.isSubbed) return true
          else return false
        case 'Beyond':
          // eslint-disable-next-line sonarjs/prefer-single-boolean-return
          if (subData?.unlocked?.includes(blogData[index].id)) return true
          else return false
        default: return true
      }
    }
  
    const BlogText = ({ index, isMobile }) => {
        
      const isStandard = blogData[index].subscriptionRequired === 'Standard'
      const lockIconSize = 
        isMobile
        ? 12
        : 16
  
      return (
        <>
          {isMobile ? (
            <div className="blog-preview-text flex flex-col p-5 mb-4">
              <h2 className="cursor-pointer mb-blogText-mob tracking-wider font-soehne-kraftig text-xs">
                {!getIsPermitted(index) && <LockDisplay
                  subscriptionRequired={blogData[index].subscriptionRequired}
                  isStandard={isStandard}
                  tag={'UNLOCK'}
                  lockIconSize={lockIconSize} />
                }
                {blogData[index].title}
              </h2>
              <hr className="content-divider" />
              <span className="font-soehne-kraftig text-xs text-light">
                {blogData[index].subtitle}
              </span>
              <div>
                <span className="font-soehne-kraftig text-light text-xs tracking-wider">
                  {blogData[index].blogType}{' '}
                </span>
                <span className="font-soehne-kraftig text-light text-xs tracking-wider">
                  BY: {blogData[index].author.toUpperCase()}
                  <br />
                  <span className="font-soehne-kraftig">{blogData[index].datePublished}</span>
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col mt-5">
              <Link passHref href={`/muses/${blogData[index].blogUrl}`}>
                <h2 className="cursor-pointer mb-blogText dt-800:mb-4 font-soehne-leight text-3xl dt-800:text-2xl">
                  {!getIsPermitted(index) && <LockDisplay
                    subscriptionRequired={blogData[index].subscriptionRequired}
                    isStandard={isStandard}
                    tag={'UNLOCK'}
                    lockIconSize={lockIconSize} />
                  }
                  {blogData[index].title}
                </h2>
              </Link>
              <hr className="content-divider" />
              <span className="mb-blogText dt-800:mb-4 font-soehne-xleight text-lg dt-800:text-base">
                {blogData[index].subtitle}
              </span>
              <span className="mb-blogText text-olive opacity-50 dt-800:mb-4 font-soehne-kraftig text-xs">
                {blogData[index].author}
              </span>
              <span className="font-soehne-kraftig text-sm dt-800:text-xs">
                {blogData[index].datePublished}
              </span>
            </div>
          )}
        </>
      )
    }

    if (error) return <div className="mt-10"><Error message={error} /></div>

    return (
      <>
        <Head>
          <title>Beyond The Pines</title>
          <meta
            name="description"
            content="Beyond The Pines Magazine: breathtaking photography and visual provocation that stimulates thoughts, ideas, & conversations. "
          />
          <meta
            name="keywords"
            content="Beyond The Pines, Beyond The Pines Magazine, Beyond The Pines Mag, magazine, mag, BTP, BTP magazine, BTP mag, "
          />
        </Head>
        <ScrollableAnchor id='blogs-start'>
          <div className={`${isMobile ? '' : 'mt-height-2 mb-20'}`}>
            <div className='flex w-full'>
              <div className="w-full">
                <InfiniteScroll
                  dataLength={itemCount}
                  next={displayMoreBlogs}
                  scrollThreshold={"200px"}
                  hasMore={hasMore}
                  loader={
                    <div className="w-full flex justify-center items-center py-8">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-olive"></div>
                        <span className="text-gray-600 font-soehne-leight">Loading more posts...</span>
                      </div>
                    </div>
                  }
                  className="flex flex-row flex-wrap gap-4 justify-center"
                >
                  {blogCountArray.map((index) => {

                  if (!blogData[index]) return null

                  return (
                    <div key={index} className="blog-preview dt:w-[22%] mb:w-1/2 cursor-pointer relative">
                      <div className="flex mb:flex-col w-full">
                        <Link passHref href={`/muses/${blogData[index].blogUrl}`}>
                          <div className="
                            blog-preview-container
                            relative
                            group
                            cursor-pointer
                            transition-transform duration-300
                            hover:scale-[1.02]
                          ">
                            <div className="
                              blog-preview-image-container
                              flex-shrink-0
                              w-full
                              dt:h-[60vh]
                              mb:h-[35vh]
                              overflow-hidden
                            ">
                              <HomePageImage
                                isMobile={isMobile}
                                src={urlParser(blogData[index].thumbnail, {
                                  width: isMobile ? 600 : 800,
                                  height: isMobile ? 800 : 1000,
                                  format: 'webp',
                                  quality: 85
                                })}
                                alt={'Thumbnail for the blog entitled: ' + blogData[index].title}
                                className="
                                  blog-preview-image
                                  cursor-pointer
                                  object-cover
                                  block
                                  w-full
                                  h-full
                                "
                              />
                            </div>

                            {/* Hover Overlay */}
                            <div className="
                              absolute inset-0
                              bg-black bg-opacity-60
                              opacity-0 group-hover:opacity-100
                              transition-opacity duration-300
                              flex flex-col justify-end
                              p-6
                              text-white
                            ">
                              <div className="space-y-2">
                                {!getIsPermitted(index) && (
                                  <div className="flex items-center space-x-2 mb-2">
                                    <LockDisplay
                                      subscriptionRequired={blogData[index].subscriptionRequired}
                                      isStandard={blogData[index].subscriptionRequired === 'Standard'}
                                      tag={'UNLOCK'}
                                      lockIconSize={16}
                                    />
                                  </div>
                                )}
                                <h3 className="font-soehne-kraftig text-lg dt:text-xl mb-1">
                                  {blogData[index].title}
                                </h3>
                                <p className="font-soehne-xleight text-sm dt:text-base opacity-90">
                                  {blogData[index].subtitle}
                                </p>
                                <div className="flex items-center space-x-4 text-xs opacity-75">
                                  <span>{blogData[index].blogType}</span>
                                  <span>•</span>
                                  <span>{blogData[index].author}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )
                })}
              </InfiniteScroll>
              </div>
            </div>
            
          </div>
        </ScrollableAnchor>
      </>
    )
  }

  export default Grid;
